import { FormSection as Section } from "$components/form-section";
import { SelectField, TextField } from "$components/rhf-fields";
import type { Territory } from "$global-providers/config-provider/provider";
import useNextOfKinRelationshipTranslation from "$routes/new/_lib/use-next-of-kin-relationship-translation";
import { Collapse } from "@material-ui/core";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";
import { RHFAddressFields } from "../../step1/_lib/RHFAddressFields";

const NextOfKinRelationshipSelectInput = ({ name }) => {
  const { formatMessage } = useIntl();
  const { nextOfKinRelationships } = useNextOfKinRelationshipTranslation();

  const label = formatMessage({
    id: "patient.nextOfKin.relationship.label",
    defaultMessage: "Relationship",
  });

  return (
    <SelectField
      fullWidth
      name={name}
      label={label}
      inputLabelProps={{
        "aria-label": "Next of kin relationship",
      }}
      data-testid="nextOfKinRelationship"
      options={nextOfKinRelationships.map(({ name, code }) => ({
        label: name,
        value: code,
      }))}
    />
  );
};

const getDeepValues = (obj: unknown) =>
  Object.values(obj).flatMap((v) =>
    typeof v === "object" ? getDeepValues(v) : v,
  );

export const NextOfKinSection: FC<{
  territory: Territory;
  fieldNames: {
    fullName: string;
    phoneNumber: string;
    relationship: string;
    address: {
      country: string;
      addressLine1: string;
      addressLine2: string;
      addressLine3: string;
      addressLine4: string;
    };
  };
}> = ({ territory, fieldNames }) => {
  const { watch, getValues } = useFormContext();
  const { formatMessage } = useIntl();
  const { trigger } = useFormContext();
  const [showSupplementaryFields, setShowSupplementaryFields] = useState(
    // Ensure it's in the correct state on first render.
    !!getValues(fieldNames.fullName),
  );

  // This pattern subscribes to all form updates *without* re-rendering the
  // component each time any value changes.
  useEffect(() => {
    // Convert the dot notation string to an array of keys.
    const fullNameKeys = fieldNames.fullName.split(".");
    const allFieldKeys = getDeepValues(fieldNames);
    const { unsubscribe } = watch((form) => {
      // Recursively access the nested value in the form object.
      const value = fullNameKeys.reduce((a, b) => a[b], form);
      if (!value) {
        // None of the fields in this form should be invalid if the full name is
        // empty.
        trigger(allFieldKeys);
        setShowSupplementaryFields(false);
      } else if (!showSupplementaryFields) {
        setShowSupplementaryFields(true);
      }
    });
    return () => unsubscribe();
  }, [fieldNames, watch, trigger, showSupplementaryFields]);

  return (
    <Section
      title={formatMessage({
        id: "patient.nextOfKin.heading",
        defaultMessage: "Next Of Kin / Guardian",
      })}
    >
      <TextField
        fullWidth
        name={fieldNames.fullName}
        label={formatMessage({
          id: "patient.nextOfKin.fullName.label",
          defaultMessage: "Full Name (First, Middle, Last)",
        })}
        InputLabelProps={{
          "aria-label": "Next of kin full name",
        }}
        data-testid="nextOfKinFullName"
      />
      <Collapse in={showSupplementaryFields}>
        <TextField
          fullWidth
          name={fieldNames.phoneNumber}
          required={showSupplementaryFields}
          type="tel"
          label={formatMessage({
            id: "patient.nextOfKin.phoneNumber.label",
            defaultMessage: "Phone Number",
          })}
          InputLabelProps={{
            "aria-label": "Next of kin phone number",
          }}
          data-testid="nextOfKinPhoneNumber"
        />
        <NextOfKinRelationshipSelectInput name={fieldNames.relationship} />
        <RHFAddressFields
          fieldNames={fieldNames.address}
          territory={territory}
        />
      </Collapse>
    </Section>
  );
};
